import { apiEndpoint } from "../utils/env";

/**
 * Annotations on an archived document.
 *
 * Addressed by <b>(databaseName, documentId)</b> — the opposite choice to the discussion, which keys on
 * archiveNo. A comment is about the document and should survive a new version; a rectangle at (72, 640) is
 * about the bytes it was measured on, and carrying it onto a re-paginated revision would move it silently
 * onto the wrong words. So a mark belongs to one version, and a new version starts clean.
 *
 * Geometry travels in <b>PDF user-space points, origin bottom-left</b> — never browser pixels. The viewer
 * converts with the pdf.js viewport, so what is stored is the page's own coordinates and a mark drawn at
 * one zoom on one screen lands correctly at any other.
 */
async function call(path, token, options = {}) {
  const headers = { ...(options.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (options.body) headers["Content-Type"] = "application/json";

  const res = await fetch(apiEndpoint(path), { ...options, headers });
  const txt = await res.text();
  let json = null;
  try { json = txt ? JSON.parse(txt) : null; } catch { json = txt; }

  if (!res.ok) {
    const msg = (json && json.message) || (typeof json === "string" ? json : res.statusText);
    const err = new Error(msg || `Request failed: ${res.status}`);
    err.status = res.status;
    err.code = json && json.code;
    err.details = json;
    throw err;
  }
  return json;
}

const q = encodeURIComponent;

/**
 * The marks on ONE version, plus `onOtherVersions` — a count of marks that live on a different version of
 * the same document, so a reader on v3 is told that v1 was marked up instead of silently seeing nothing.
 * A mark whose `stale` flag is set was measured against bytes the row no longer points at.
 */
export function listAnnotations(token, databaseName, documentId) {
  return call(`/api/Document/Annotations?databaseName=${q(databaseName)}&documentId=${q(documentId)}`, token);
}

export function addAnnotation(token, annotation) {
  return call("/api/Document/Annotations", token, {
    method: "POST",
    body: JSON.stringify(annotation),
  });
}

/** Colour and note only — geometry is immutable once drawn. To move a mark, remove it and draw it again. */
export function updateAnnotation(token, id, { databaseName, color, note }) {
  return call(`/api/Document/Annotations/${id}`, token, {
    method: "PATCH",
    body: JSON.stringify({ databaseName, color, note }),
  });
}

/** Tombstones the mark: the row and its geometry stay, it stops being drawn. */
export function deleteAnnotation(token, id, databaseName) {
  return call(`/api/Document/Annotations/${id}?databaseName=${q(databaseName)}`, token, { method: "DELETE" });
}
